import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { AlertCircle } from "lucide-react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema } from "schema"
import z from "zod"
import { UpdateCampaignSchemaFrontend } from "../hooks/useCampaignForm"
import { useLaunchTooltip } from "../hooks/useLaunchTooltip"
import { useChannelWarnings } from "./Detail/Configuration/hooks/useChannelWarnings"

type Props = {
  showTooltip: boolean
  render: (hasErrors: boolean) => React.ReactNode
  form: UseFormReturn<z.infer<typeof UpdateCampaignSchemaFrontend>>
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
}

export function LaunchTooltip({ showTooltip, render, form, campaign }: Props) {
  const { t } = useTranslation()

  const { errors } = useLaunchTooltip(form, campaign)
  const { shouldShowLanguageWarning } = useChannelWarnings(form)

  const combinedErrors = [
    ...(errors ?? []),
    ...(shouldShowLanguageWarning
      ? [
          {
            icon: AlertCircle,
            translation: t("campaigns.builder.validation.defaultLanguageMismatch"),
          },
        ]
      : []),
  ]
  const hasErrors = combinedErrors.length > 0

  return (
    <Tooltip open={showTooltip && hasErrors ? undefined : false}>
      <TooltipTrigger asChild>{render(hasErrors)}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="end"
        variant="card"
        sideOffset={0}
        className="max-w-sm sm:max-w-lg rounded-xl flex flex-col gap-4 py-5 px-6"
      >
        <div className="flex flex-col gap-1">
          <span className="text-lg font-medium">
            {t("campaigns.builder.validation.missingConfiguration.title")}
          </span>
          <span className="text-base">
            {t("campaigns.builder.validation.missingConfiguration.description")}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          {combinedErrors.map(({ icon: Icon, translation }) => (
            <div key={translation} className="flex items-center gap-2">
              <Icon className="w-6 h-6 text-chart-primary" />
              <span className="text-base">{translation}</span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
